import { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, TrendingUp, AlertCircle, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

export default function ParentEngagementDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/parent-engagement/scores`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(res.data);
      } catch { /* silent */ }
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) return <div className="animate-pulse h-40 bg-zinc-900/50 rounded-xl" />;
  if (!data) return null;

  const filtered = filter === 'all' ? data.scores : data.scores.filter(s => s.engagement_level === filter);
  const levelColor = { high: 'text-green-400', medium: 'text-amber-400', low: 'text-red-400', none: 'text-zinc-500' };
  const levelBg = { high: 'bg-green-500/20', medium: 'bg-amber-500/20', low: 'bg-red-500/20', none: 'bg-zinc-800' };

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="parent-engagement">
      <div 
        className="p-5 flex items-center justify-between cursor-pointer hover:bg-zinc-800/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
            <Users className="h-5 w-5 text-purple-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">Parent Engagement</h3>
            <p className="text-xs text-zinc-400">{data.summary?.total_parents || 0} parents tracked</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Summary pills */}
          <div className="hidden sm:flex items-center gap-2">
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 font-medium">{data.summary?.high_engagement} high</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 font-medium">{data.summary?.medium_engagement} med</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 font-medium">{data.summary?.low_engagement} low</span>
          </div>
          {expanded ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-zinc-800">
          {/* Filter tabs */}
          <div className="px-5 pt-3 flex gap-2">
            {['all', 'low', 'medium', 'high'].map(f => (
              <button
                key={f}
                onClick={(e) => { e.stopPropagation(); setFilter(f); }}
                className={`text-[10px] px-3 py-1 rounded-full font-medium uppercase tracking-wider transition-colors ${
                  filter === f ? 'bg-[#02b5ff] text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'
                }`}
              >
                {f === 'all' ? `All (${data.scores?.length})` : `${f} (${data.scores?.filter(s => s.engagement_level === f).length})`}
              </button>
            ))}
          </div>

          {/* Parent list */}
          <div className="p-4 space-y-2 max-h-[350px] overflow-y-auto">
            {filtered.map((score, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-zinc-800/40 rounded-lg hover:bg-zinc-800/60 transition-colors">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${levelBg[score.engagement_level]} ${levelColor[score.engagement_level]}`}>
                  {score.total_score}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-white truncate">{score.child_name}</p>
                  <p className="text-[11px] text-zinc-500 truncate">{score.parent_name || score.parent_email}</p>
                </div>
                {/* Score breakdown mini-bars */}
                <div className="hidden md:flex items-center gap-1">
                  {Object.entries(score.breakdown || {}).map(([key, val]) => (
                    <div key={key} className="w-8 h-1.5 bg-zinc-800 rounded-full overflow-hidden" title={`${key}: ${val.score}/${val.max}`}>
                      <div 
                        className={`h-full rounded-full ${val.score > val.max * 0.6 ? 'bg-green-500' : val.score > val.max * 0.3 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{ width: `${(val.score / val.max) * 100}%` }}
                      />
                    </div>
                  ))}
                </div>
                {/* Engagement level badge */}
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium uppercase ${levelBg[score.engagement_level]} ${levelColor[score.engagement_level]}`}>
                  {score.engagement_level}
                </span>
              </div>
            ))}
            {filtered.length === 0 && (
              <p className="text-center text-xs text-zinc-600 py-4">No parents in this category</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
